/**
=========================================================
* Trust Cargo - the carton label
=========================================================

* The printed form: a lamp in the corner for light goods, a stack of filled
* in lines, and the company name across the foot.
*
* The lines are not fixed. Every one of them - its Chinese heading, its
* English name, its unit and which column feeds it - is described by the
* screen and passed in, because these sheets change shape per supplier and a
* layout baked in here would mean a code change every time one does.
*
* Everything is in millimetres and points rather than pixels, because this
* is only ever looked at on paper - a label laid out in px comes off the
* printer at whatever size the browser felt like.
*
* Those millimetres are multiplied by `scale`. The same form is printed one
* to a sheet of A4 and four to a sheet, and type sized for the small one is
* lost on the big one - so the drawing is written once at the size of a
* 100 x 150 label and grown or shrunk with the cell it is given.
*
* The dotted rules are drawn even when a value is present. They are part of
* the form, not a placeholder for a missing value, and a label with the line
* under one field and not another reads as damaged.
*/

import PropTypes from "prop-types";

/** CJK first: the bracketed headings are Chinese and must not fall back. */
const FONT = "'Noto Sans SC', 'Microsoft YaHei', 'PingFang SC', 'SimHei', Arial, sans-serif";

/**
 * A measurement at the label's own scale.
 *
 * Rounded, because a rule at 1.2000000000000002pt is a rule the printer has
 * to think about, and left without trailing zeros so the plain size reads
 * as it was written.
 */
const at = (value, scale, unit) => `${Math.round(value * scale * 100) / 100}${unit}`;

/**
 * The lamp mark, drawn rather than linked.
 *
 * An <img> would need the file to exist somewhere the printer can reach it,
 * and this label is generated in a browser with no server behind it. Vector
 * also prints at the printer's own resolution instead of at the screen's.
 */
function Lamp({ size }) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} aria-hidden="true">
      <g stroke="#000" strokeWidth="5" strokeLinecap="round" fill="none">
        {/* rays */}
        <line x1="50" y1="4" x2="50" y2="14" />
        <line x1="18" y1="17" x2="25" y2="24" />
        <line x1="82" y1="17" x2="75" y2="24" />
        <line x1="6" y1="49" x2="16" y2="49" />
        <line x1="94" y1="49" x2="84" y2="49" />
        <line x1="18" y1="81" x2="25" y2="74" />
        <line x1="82" y1="81" x2="75" y2="74" />

        {/* the glass */}
        <circle cx="50" cy="49" r="25" />
        {/* the cap */}
        <line x1="41" y1="76" x2="59" y2="76" />
        <line x1="43" y1="83" x2="57" y2="83" />
      </g>

      <text
        x="50"
        y="45"
        textAnchor="middle"
        fontFamily={FONT}
        fontSize="15"
        fontWeight="600"
        fill="#000"
      >
        light
      </text>
      <text
        x="50"
        y="63"
        textAnchor="middle"
        fontFamily={FONT}
        fontSize="17"
        fontWeight="700"
        fill="#000"
      >
        光
      </text>
    </svg>
  );
}

Lamp.defaultProps = { size: "22mm" };
Lamp.propTypes = { size: PropTypes.string };

/** One line: the Chinese heading, the English name, and the dotted rule. */
function Line({ cn, en, value, unit, big, scale }) {
  return (
    <div style={{ marginBottom: at(big ? 4 : 3, scale, "mm") }}>
      {cn ? (
        <div
          style={{
            fontSize: at(big ? 11 : 9.5, scale, "pt"),
            fontWeight: 700,
            lineHeight: 1.25,
          }}
        >
          {cn}
        </div>
      ) : null}

      <div style={{ display: "flex", alignItems: "flex-end", gap: at(2, scale, "mm") }}>
        {en ? (
          <span
            style={{
              fontSize: at(big ? 13 : 11, scale, "pt"),
              fontWeight: 700,
              whiteSpace: "nowrap",
            }}
          >
            {en}
          </span>
        ) : null}

        {/* the unit sits above the rule, as on the printed form */}
        <span
          style={{
            flex: 1,
            minWidth: 0,
            borderBottom: `${at(1.2, scale, "pt")} dotted #000`,
            paddingBottom: at(0.6, scale, "mm"),
            fontSize: at(big ? 13 : 11, scale, "pt"),
            textAlign: "center",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {[value, unit].filter(Boolean).join(" ") || " "}
        </span>
      </div>
    </div>
  );
}

Line.defaultProps = { cn: "", en: "", value: "", unit: "", big: false, scale: 1 };
Line.propTypes = {
  cn: PropTypes.string,
  en: PropTypes.string,
  value: PropTypes.string,
  unit: PropTypes.string,
  big: PropTypes.bool,
  scale: PropTypes.number,
};

function Label({ lines, title, company, lamp, scale }) {
  return (
    <div
      className="tc-label"
      style={{
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        padding: at(5, scale, "mm"),
        fontFamily: FONT,
        color: "#000",
        background: "#fff",
      }}
    >
      {/**
       * The lamp is the light-goods mark. On anything else it is not just
       * unnecessary, it is wrong - so the corner is left empty rather than
       * filled with something that says the carton is something it is not.
       * The space is held either way, so the three lines sit at the same
       * height on every label in a run.
       */}
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: at(22, scale, "mm"),
        }}
      >
        {/**
         * The order code, in the space the lamp was already holding. Above
         * the fields rather than among them because it says which job the
         * carton belongs to, not what is in it - and over the full width
         * rather than in what the lamp leaves, so it sits on the label's own
         * centre line, under the company name at the foot. The lamp is taken
         * out of the flow for that: sharing a row with it would centre the
         * code in the space beside it, which is off-centre on the label.
         *
         * Its room is given up either way, so a light-goods label and a
         * plain one in the same run read the same.
         */}
        <div
          style={{
            boxSizing: "border-box",
            width: "100%",
            paddingLeft: lamp ? at(24, scale, "mm") : 0,
            paddingRight: lamp ? at(24, scale, "mm") : 0,
            textAlign: "center",
            fontSize: at(19, scale, "pt"),
            fontWeight: 800,
            letterSpacing: "0.04em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {title}
        </div>

        {lamp ? (
          <div style={{ position: "absolute", top: 0, right: 0 }}>
            <Lamp size={at(22, scale, "mm")} />
          </div>
        ) : null}
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        {lines.map((l) => (
          <Line
            key={l.id}
            cn={l.cn}
            en={l.en}
            value={l.value}
            unit={l.unit}
            big={l.big}
            scale={scale}
          />
        ))}
      </div>

      <div
        style={{
          borderTop: `${at(1.5, scale, "pt")} solid #000`,
          paddingTop: at(2, scale, "mm"),
          textAlign: "center",
          fontSize: at(20, scale, "pt"),
          fontWeight: 800,
          letterSpacing: "0.06em",
          whiteSpace: "nowrap",
          overflow: "hidden",
        }}
      >
        {company}
      </div>
    </div>
  );
}

Label.defaultProps = { lines: [], title: "", company: "阿納斯公司", lamp: false, scale: 1 };

Label.propTypes = {
  lines: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      cn: PropTypes.string,
      en: PropTypes.string,
      value: PropTypes.string,
      unit: PropTypes.string,
      big: PropTypes.bool,
    })
  ),
  // the order code, printed across the head of the label
  title: PropTypes.string,
  company: PropTypes.string,
  // shown only for light goods
  lamp: PropTypes.bool,
  // the size of the cell this label is printed into, 1 being a 100 x 150 label
  scale: PropTypes.number,
};

export default Label;
